import { useParams } from "react-router-dom";
import { useFeatureFlag } from "@qualio/ui-components";
import { FeatureFlags } from "@design-controls/types";

import { useRetrieveDesignElementById } from "./useRetrieveDesignElementById";
import { useRetrieveDesignElementHistory } from "./useRetrieveDesignElementHistory";
import { useRetrieveDesignElementVersions } from "./useRetrieveDesignElementVersions";
import {
  findDesignElementVersionByLabel,
  isDesignElementVersionLabel,
} from "../domain/designElementVersion";

type DrawerParams = {
  selectedItemId: string;
  version: string;
};

export const useDeleteElementDrawer = () => {
  const { selectedItemId, version: versionParam = "v0" } =
    useParams<DrawerParams>() as DrawerParams;
  const versioningEnabled = Boolean(
    useFeatureFlag()[FeatureFlags.MAJOR_MINOR_VERSION_UI],
  );

  // A major.minor label ("v2.0") has to be resolved to the internal version
  // ("v12") through the versions list before the element can be fetched.
  const isVersionLabel = isDesignElementVersionLabel(versionParam);
  const designElementVersions = useRetrieveDesignElementVersions(
    selectedItemId,
    versioningEnabled || isVersionLabel,
  );
  const version = isVersionLabel
    ? findDesignElementVersionByLabel(
        designElementVersions.data ?? [],
        versionParam,
      )?.version
    : versionParam;

  const designElement = useRetrieveDesignElementById(
    selectedItemId,
    version ?? versionParam,
    version !== undefined,
  );
  const designElementHistory = useRetrieveDesignElementHistory(selectedItemId);

  return {
    isLoading:
      designElement.isLoading ||
      designElementHistory.isLoading ||
      (isVersionLabel && designElementVersions.isLoading),
    designElement: designElement.data,
    designElementHistory: designElementHistory.data,
    designElementVersions,
    version: version ?? versionParam,
  };
};
